// Single typed fetch client for the admin surface.
//
// Always same-origin: in production one CloudFront distribution serves the SPA
// from S3 and forwards /api/* to the API Lambda; in dev Vite's `server.proxy`
// does the same (ADR-017). That is what keeps the httpOnly session cookie
// first-party — no CORS, no SameSite=None. `credentials:"include"` is belt and
// braces. Nothing here knows the backend's real origin, by design.

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(status: number, message: string, code: string | null = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  // Any JSON-serialisable payload. Typed at the call site by the endpoint fn.
  body?: unknown;
  signal?: AbortSignal;
}

/**
 * Perform an API request and return the parsed JSON, typed as T.
 * Throws {@link ApiError} on any non-2xx response, carrying the backend's
 * `code` (admin surface) or `message` when present.
 */
export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = `/api${path}`;
  const { method = "GET", body, signal } = opts;
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      credentials: "include",
      headers: body ? { "Content-Type": "application/json" } : undefined,
      body: body ? JSON.stringify(body) : undefined,
      signal,
      cache: "no-store",
    });
  } catch (err) {
    throw new ApiError(0, err instanceof Error ? err.message : "Network error");
  }

  const text = await res.text();
  const data: unknown = text ? safeParse(text) : null;

  if (!res.ok) {
    const obj = (data ?? {}) as Record<string, unknown>;
    // Admin surface returns {code, message}; FastAPI HTTPException returns
    // {detail}. Accept either so authoring 4xx messages surface.
    const message =
      typeof obj.message === "string"
        ? obj.message
        : typeof obj.detail === "string"
          ? obj.detail
          : `Request failed (${res.status})`;
    const code = typeof obj.code === "string" ? obj.code : null;
    throw new ApiError(res.status, message, code);
  }

  return data as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}
